import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useTheme } from "styled-components";
import { showErrorToast, showSuccessToast } from "../utils/toastHelpers";
import type { AxiosRequestConfig } from "axios";
import {
  scriptsApi,
  leadsApi,
  dataApi,
  botApi,
  typingApi,
  whatsappApi,
  api,
} from "../services/api";
import {
  CreateLeadRequest,
  UpdateLeadRequest,
  CreateVendaRequest,
  CreateInteracaoRequest,
  BotMessageRequest,
  BotValidationRequest,
  WhatsAppMessageRequest,
  WhatsAppMediaRequest,
} from "../types/api";
import type { GeneratedScript } from "../types/aiGeneration";

// React Query Keys
export const queryKeys = {
  scripts: (userId?: string) => ["scripts", userId || "default"] as const,
  script: (tipo: string, nome: string, userId?: string) => ["scripts", tipo, nome, userId || "default"] as const,
  scriptStructure: ["scripts", "structure"] as const,
  userCustomizations: (userId: string) => ["scripts", "customizations", userId] as const,
  scriptStats: ["scripts", "stats"] as const,
  leads: ["leads"] as const,
  leadsByStatus: (status: string) => ["leads", "status", status] as const,
  leadByPhone: (phone: string) => ["leads", "phone", phone] as const,
  leadsFollowUp: ["leads", "followup"] as const,
  dashboardStats: ["dashboard", "stats"] as const,
  vendas: ["vendas"] as const,
  vendasTotal: ["vendas", "total"] as const,
  interacoes: ["interacoes"] as const,
  interacoesBotUltimas24h: ["interacoes", "bot24h"] as const,
  leadsNovosHoje: ["leads", "novosHoje"] as const,
  vendasHoje: ["vendas", "hoje"] as const,
  botConfig: ["bot", "config"] as const,
  botUserStats: (userId: string) => ["bot", "stats", userId] as const,
  media: ["media"] as const,
  mediaByContext: (context: string) => ["media", "context", context] as const,
  mediaByType: (type: string) => ["media", "type", type] as const,
  mediaStats: ["media", "stats"] as const,
  typingStats: ["typing", "stats"] as const,
  typingProfiles: ["typing", "profiles"] as const,
  typingUserProfile: (userId: string) => ["typing", "profile", userId] as const,
};

export const useApi = () => {
  const request = useCallback(
    async <T = unknown>(url: string, config: AxiosRequestConfig = {}) => {
      const { method = "get", ...rest } = config;
      const response = await api.request<T>({
        url,
        method,
        ...rest,
      });
      return response.data;
    },
    []
  );

  return { request };
};

// Scripts Hooks
export const useScripts = (userId?: string) => {
  return useQuery({
    queryKey: queryKeys.scripts(userId),
    queryFn: () => scriptsApi.getAll(userId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useScript = (tipo: string, nome: string, userId?: string) => {
  return useQuery({
    queryKey: queryKeys.script(tipo, nome, userId),
    queryFn: () => scriptsApi.getScript(tipo, nome, userId),
    enabled: !!tipo && !!nome,
  });
};

export const useScriptStructure = () => {
  return useQuery({
    queryKey: queryKeys.scriptStructure,
    queryFn: scriptsApi.getStructure,
  });
};

export const useUserCustomizations = (userId: string) => {
  return useQuery({
    queryKey: queryKeys.userCustomizations(userId),
    queryFn: () => scriptsApi.getUserCustomizations(userId),
    enabled: !!userId,
  });
};

export const useScriptStats = () => {
  return useQuery({
    queryKey: queryKeys.scriptStats,
    queryFn: scriptsApi.getSystemStats,
  });
};

export const useUpdateScript = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: ({
      tipo,
      nome,
      conteudo,
      userId,
    }: {
      tipo: string;
      nome: string;
      conteudo: string;
      userId?: string;
    }) => scriptsApi.updateScript(tipo, nome, conteudo, userId),
    onSuccess: (data, variables) => {
      // Invalidar cache específico do usuário
      queryClient.invalidateQueries({ queryKey: queryKeys.scripts(variables.userId) });
      if (variables.userId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.userCustomizations(variables.userId) });
      }

      // ✅ SYNC REVERSO: Script editado → Invalida configs dinâmicas
      if (variables.userId) {
        if (variables.tipo === 'mentalidade') {
          queryClient.invalidateQueries({ queryKey: ['persona-config', variables.userId] });
          console.log('🔄 Config de persona invalidada (sync reverso script→config)');
        }
      }
    },
    onError: (error: any) => {
      showErrorToast(
        "Erro ao atualizar script: " +
          (error.response?.data?.message || error.message),
        theme
      );
    },
  });
};

export const useUpdateFatosProduto = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: ({ conteudo, userId }: { conteudo: string; userId?: string }) =>
      scriptsApi.updateFatosProduto(conteudo, userId),
    onSuccess: (data, variables) => {
      // Invalidar cache específico do usuário
      queryClient.invalidateQueries({ queryKey: queryKeys.scripts(variables.userId) });
      if (variables.userId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.userCustomizations(variables.userId) });
      }

      // ✅ SYNC REVERSO: fatos_produto editado → Invalida config de produto
      if (variables.userId) {
        queryClient.invalidateQueries({ queryKey: ['produto-config', variables.userId] });
        console.log('🔄 Config de produto invalidada (sync reverso script→config)');
      }
    },
    onError: (error: any) => {
      showErrorToast(
        "Erro ao atualizar fatos do produto: " +
          (error.response?.data?.message || error.message),
        theme
      );
    },
  });
};

export const useResetScript = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: ({ tipo, nome, userId }: { tipo: string; nome: string; userId: string }) =>
      scriptsApi.resetScript(tipo, nome, userId),
    onSuccess: (data, variables) => {
      // Invalidar cache específico do usuário
      queryClient.invalidateQueries({ queryKey: queryKeys.scripts(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.userCustomizations(variables.userId) });
      showSuccessToast("Script resetado para o padrão com sucesso!", theme);
    },
    onError: (error: any) => {
      showErrorToast(
        "Erro ao resetar script: " +
          (error.response?.data?.message || error.message),
        theme
      );
    },
  });
};


export const useReloadScripts = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: scriptsApi.reloadScripts,
    onSuccess: () => {
      // Invalidar cache de todos os usuários (scripts padrão foram recarregados)
      queryClient.invalidateQueries({ queryKey: ["scripts"] });
      // Não mostrar toast aqui, será mostrado no componente
    },
    onError: (error: any) => {
      showErrorToast(
        "Erro ao recarregar scripts: " +
          (error.response?.data?.message || error.message),
        theme
      );
    },
  });
};

export const useResetScripts = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: (userId: string) => {
      console.log('[useResetScripts] 🔄 Iniciando reset para userId:', userId);
      if (!userId) {
        console.error('[useResetScripts] ❌ userId está vazio!');
        throw new Error('userId é obrigatório para resetar scripts');
      }
      return scriptsApi.resetScripts(userId);
    },
    onSuccess: async (_, userId) => {
      console.log('🔄 Reset iniciado: deletando scripts e configs do backend...');

      // Invalidar cache do usuário específico - SCRIPTS TRADICIONAIS
      queryClient.invalidateQueries({ queryKey: ["scripts", userId] });

      // ✅ Invalidar CONFIGS DINÂMICAS também (produto, persona, emojis)
      queryClient.invalidateQueries({ queryKey: ['produto-config', userId] });
      queryClient.invalidateQueries({ queryKey: ['persona-config', userId] });
      queryClient.invalidateQueries({ queryKey: ['emojis-config', userId] });

      // ✅ FORÇAR refetch imediato e AGUARDAR para carregar defaults do backend
      try {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['produto-config', userId] }),
          queryClient.refetchQueries({ queryKey: ['persona-config', userId] }),
          queryClient.refetchQueries({ queryKey: ['emojis-config', userId] }),
          queryClient.refetchQueries({ queryKey: ["scripts", userId] })
        ]);
        console.log('✅ Reset completo: scripts tradicionais + configs dinâmicas recarregadas com defaults do backend');
      } catch (refetchError) {
        console.error('⚠️ Erro ao refetch após reset:', refetchError);
        // Não falhar o reset, pois os dados serão recarregados no próximo acesso
      }
    },
    onError: (error: unknown) => {
      // Não mostra erro de 404 (primeira vez sem personalização)
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (err?.response?.status !== 404) {
        showErrorToast(
          "Erro ao redefinir scripts: " +
            (err.response?.data?.message || err.message || "Erro desconhecido"),
          theme
        );
      }
    },
  });
};

export const useApplyGeneratedScripts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, scripts }: { userId: string; scripts: GeneratedScript[] }) =>
      scriptsApi.applyGeneratedScripts(scripts, userId),
    onSuccess: (_, { userId }) => {
      // Invalidar cache de scripts do usuário para forçar reload completo
      queryClient.invalidateQueries({ queryKey: queryKeys.scripts(userId) });
      // Também invalida queries genéricas de scripts
      queryClient.invalidateQueries({ queryKey: ["scripts"] });
      // Força refetch imediato
      queryClient.refetchQueries({ queryKey: queryKeys.scripts(userId) });
    },
  });
};

// Leads Hooks
export const useLeads = () => {
  return useQuery({
    queryKey: queryKeys.leads,
    queryFn: leadsApi.getAll,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useLeadsByStatus = (status: string) => {
  return useQuery({
    queryKey: queryKeys.leadsByStatus(status),
    queryFn: () => leadsApi.getByStatus(status),
    enabled: !!status,
  });
};

export const useLeadsFollowUp = () => {
  return useQuery({
    queryKey: queryKeys.leadsFollowUp,
    queryFn: leadsApi.getFollowUp,
  });
};

export const useCreateLead = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: (lead: CreateLeadRequest) => leadsApi.create(lead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      showSuccessToast("Lead criado com sucesso!", theme);
    },
    onError: (error: any) => {
      showErrorToast(
        "Erro ao criar lead: " +
          (error.response?.data?.message || error.message),
        theme
      );
    },
  });
};

export const useUpdateLead = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: ({ id, lead }: { id: number; lead: UpdateLeadRequest }) =>
      leadsApi.update(id, lead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      showSuccessToast("Lead atualizado!", theme);
    },
    onError: (error: any) => {
      showErrorToast(
        "Erro ao atualizar lead: " +
          (error.response?.data?.message || error.message),
        theme
      );
    },
  });
};

export const useDeleteLead = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  return useMutation({
    mutationFn: (id: number) => leadsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      showSuccessToast("Lead excluído!", theme);
    },
    onError: (error: any) => {
      showErrorToast(
        "Erro ao excluir lead: " +
          (error.response?.data?.message || error.message),
        theme
      );
    },
  });
};

// Dashboard Hooks
export const useDashboardStats = () => {
  return useQuery({
    queryKey: queryKeys.dashboardStats,
    queryFn: leadsApi.getDashboard,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Vendas Hooks
export const useDeleteVenda = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => dataApi.deleteVenda(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendas });
      queryClient.invalidateQueries({ queryKey: queryKeys.vendasTotal });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      toast.success('Venda excluída!');
    },
    onError: (error: any) => {
      toast.error(
        'Erro ao excluir venda: ' +
          (error.response?.data?.message || error.message)
      );
    },
  });
};
export const useUpdateVenda = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, venda }: { id: number; venda: CreateVendaRequest }) => dataApi.updateVenda(id, venda),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendas });
      queryClient.invalidateQueries({ queryKey: queryKeys.vendasTotal });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      toast.success('Venda atualizada!');
    },
    onError: (error: any) => {
      toast.error(
        'Erro ao atualizar venda: ' +
          (error.response?.data?.message || error.message)
      );
    },
  });
};
export const useVendas = () => {
  return useQuery({
    queryKey: queryKeys.vendas,
    queryFn: dataApi.getVendas,
    staleTime: 2 * 60 * 1000,
  });
};

export const useVendasTotal = () => {
  return useQuery({
    queryKey: queryKeys.vendasTotal,
    queryFn: dataApi.getTotalVendas,
  });
};

export const useCreateVenda = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (venda: CreateVendaRequest) => dataApi.createVenda(venda),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendas });
      queryClient.invalidateQueries({ queryKey: queryKeys.vendasTotal });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      toast.success("Venda registrada!");
    },
    onError: (error: any) => {
      toast.error(
        "Erro ao registrar venda: " +
          (error.response?.data?.message || error.message)
      );
    },
  });
};

// Interações Hooks
// Agora o backend já retorna stacks agrupados!
export const useInteracoesRecentes = () => {
  return useQuery({
    queryKey: queryKeys.interacoes,
    queryFn: dataApi.getInteracoesRecentesStacked,
    refetchInterval: 10000, // Refetch every 10 seconds
  });
};

export const useCreateInteracao = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (interacao: CreateInteracaoRequest) =>
      dataApi.createInteracao(interacao),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.interacoes });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
    onError: (error: any) => {
      toast.error(
        "Erro ao registrar interação: " +
          (error.response?.data?.message || error.message)
      );
    },
  });
};

export const useInteracoesBotUltimas24h = () => {
  return useQuery({
    queryKey: queryKeys.interacoesBotUltimas24h,
    queryFn: dataApi.getInteracoesBotUltimas24h,
    refetchInterval: 5000,
  });
};

export const useLeadsNovosHoje = () => {
  return useQuery({
    queryKey: queryKeys.leadsNovosHoje,
    queryFn: leadsApi.getLeadsNovosHoje,
    refetchInterval: 5000, // Refetch every 5 seconds
  });
};

export const useVendasHoje = () => {
  return useQuery({
    queryKey: queryKeys.vendasHoje,
    queryFn: dataApi.getVendasHoje,
    refetchInterval: 5000, // Refetch every 5 seconds
  });
};

// Bot Hooks
export const useBotConfig = () => {
  return useQuery({
    queryKey: queryKeys.botConfig,
    queryFn: botApi.getConfig,
  });
};

export const useSendBotMessage = () => {
  return useMutation({
    mutationFn: (request: BotMessageRequest) => botApi.sendMessage(request),
    // Removido toast automático para permitir controle personalizado
  });
};

export const useValidateBotMessage = () => {
  return useMutation({
    mutationFn: (request: BotValidationRequest) =>
      botApi.validateMessage(request),
  });
};

export const useClearBotContext = () => {
  return useMutation({
    mutationFn: (userId: string) => botApi.clearContext(userId),
    onSuccess: () => {
      toast.success("Contexto limpo!");
    },
    onError: (error: any) => {
      toast.error(
        "Erro ao limpar contexto: " +
          (error.response?.data?.message || error.message)
      );
    },
  });
};

// WhatsApp Hooks
export const useSendWhatsAppMessage = () => {
  return useMutation({
    mutationFn: (request: WhatsAppMessageRequest) =>
      whatsappApi.sendMessage(request),
    onSuccess: () => {
      toast.success("Mensagem WhatsApp enviada!");
    },
    onError: (error: any) => {
      toast.error(
        "Erro ao enviar mensagem WhatsApp: " +
          (error.response?.data?.message || error.message)
      );
    },
  });
};

export const useSendWhatsAppMedia = () => {
  return useMutation({
    mutationFn: (request: WhatsAppMediaRequest) =>
      whatsappApi.sendMedia(request),
    onSuccess: () => {
      toast.success("Mídia enviada!");
    },
    onError: (error: any) => {
      toast.error(
        "Erro ao enviar mídia: " +
          (error.response?.data?.message || error.message)
      );
    },
  });
};

// Typing Profiles Hooks
export const useTypingStats = () => {
  return useQuery({
    queryKey: queryKeys.typingStats,
    queryFn: typingApi.getStats,
  });
};

export const useTypingProfiles = () => {
  return useQuery({
    queryKey: queryKeys.typingProfiles,
    queryFn: typingApi.getAllProfiles,
  });
};

export const useCalculateTypingDelay = () => {
  return useMutation({
    mutationFn: ({ userId, message }: { userId: string; message: string }) =>
      typingApi.calculateDelay(userId, message),
  });
};

export const useSimulateTyping = () => {
  return useMutation({
    mutationFn: ({
      userId,
      message,
      iterations,
    }: {
      userId: string;
      message: string;
      iterations: number;
    }) => typingApi.simulateTyping(userId, message, iterations),
  });
};
